<script setup lang="ts">
import { computed } from 'vue'
import { Dieline } from '@/Composables/useCustomSizeLayout'
import DielineShape from '@/Pages/CustomSize/DielineShape.vue'

const props = defineProps<{
    dieline: Dieline
    safeMargin: number
}>()

const formatCm = (value: number) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(value)

const outerWidth = computed(() => props.dieline.width + 2 * props.safeMargin)
const outerHeight = computed(() => props.dieline.height + 2 * props.safeMargin)
const fontSize = computed(() => Math.max(outerWidth.value, outerHeight.value) * 0.03)
const innerFontSize = computed(() => fontSize.value * 0.75)
const labelGutter = computed(() => fontSize.value * 3.8)
const arrowSize = computed(() => fontSize.value * 0.35)
const labelGap = computed(() => fontSize.value * 0.2)
const dielineArrowOffset = computed(() => fontSize.value * 0.6)
const safeAreaArrowOffset = computed(() => fontSize.value * 2.2)
const viewBoxPadding = computed(() => fontSize.value * 0.5)

const dielineColor = 'rgb(0, 161, 75)'
const safeAreaColor = '#b0a80c'

const viewBox = computed(() =>
    `${-labelGutter.value} ${-labelGutter.value} ${outerWidth.value + labelGutter.value + viewBoxPadding.value} ${outerHeight.value + labelGutter.value + viewBoxPadding.value}`
)

const horizontalArrow = (startX: number, endX: number, y: number) => {
    const size = arrowSize.value
    return `M${startX} ${y} H${endX}`
        + ` M${startX + size} ${y - size / 2} L${startX} ${y} L${startX + size} ${y + size / 2}`
        + ` M${endX - size} ${y - size / 2} L${endX} ${y} L${endX - size} ${y + size / 2}`
}

const verticalArrow = (x: number, startY: number, endY: number) => {
    const size = arrowSize.value
    return `M${x} ${startY} V${endY}`
        + ` M${x - size / 2} ${startY + size} L${x} ${startY} L${x + size / 2} ${startY + size}`
        + ` M${x - size / 2} ${endY - size} L${x} ${endY} L${x + size / 2} ${endY - size}`
}

const columnStarts = computed(() => {
    let offset = props.safeMargin
    return props.dieline.columns.map((column) => {
        const start = offset
        offset += column.size
        return start
    })
})

const widthDimension = (start: number, width: number, offset: number, color: string) => {
    const y = -offset
    return {
        path: horizontalArrow(start, start + width, y),
        textX: start + width / 2,
        textY: y - labelGap.value,
        transform: undefined,
        text: `${formatCm(width)} cm`,
        color,
    }
}

const heightDimension = (start: number, height: number, offset: number, color: string) => {
    const x = -offset
    const textX = x - labelGap.value
    const textY = start + height / 2
    return {
        path: verticalArrow(x, start, start + height),
        textX,
        textY,
        transform: `rotate(-90 ${textX} ${textY})`,
        text: `${formatCm(height)} cm`,
        color,
    }
}

const dimensions = computed(() => [
    widthDimension(props.safeMargin, props.dieline.width, dielineArrowOffset.value, dielineColor),
    heightDimension(props.safeMargin, props.dieline.height, dielineArrowOffset.value, dielineColor),
    widthDimension(0, outerWidth.value, safeAreaArrowOffset.value, safeAreaColor),
    heightDimension(0, outerHeight.value, safeAreaArrowOffset.value, safeAreaColor),
])

const boxLabels = computed(() => {
    const [topFlap, body] = props.dieline.rows
    const panelIndex = props.dieline.columns.findIndex((column) => column.label === 'Panel')
    const panel = props.dieline.columns[panelIndex]
    const side = props.dieline.columns[panelIndex + 1]
    if (!topFlap || !body || !panel || !side) {
        return []
    }

    const bodyTop = props.safeMargin + topFlap.size
    const bodyCenter = bodyTop + body.size / 2
    const panelStart = columnStarts.value[panelIndex]
    const panelEnd = panelStart + panel.size
    const gap = labelGap.value
    const lengthX = panelStart + gap

    return [
        {
            x: lengthX,
            y: bodyCenter,
            baseline: 'hanging',
            transform: `rotate(-90 ${lengthX} ${bodyCenter})`,
            text: `${formatCm(body.size)} cm`,
        },
        {
            x: panelStart + panel.size / 2,
            y: bodyTop + body.size - gap,
            baseline: 'auto',
            transform: undefined,
            text: `${formatCm(panel.size)} cm`,
        },
        {
            x: panelEnd + side.size / 2,
            y: bodyTop + gap,
            baseline: 'hanging',
            transform: undefined,
            text: `${formatCm(side.size)} cm`,
        },
    ]
})
</script>

<template>
    <svg :viewBox="viewBox" class="h-auto max-h-[420px] w-full" preserveAspectRatio="xMidYMid meet">
        <rect :width="outerWidth" :height="outerHeight" fill="#fff200" stroke="#a16207" stroke-width="1"
            vector-effect="non-scaling-stroke" />
        <DielineShape :dieline="dieline" :x="safeMargin" :y="safeMargin" />

        <g v-for="(dimension, index) in dimensions" :key="`dimension-${index}`">
            <path :d="dimension.path" :stroke="dimension.color" stroke-width="1.5" fill="none"
                vector-effect="non-scaling-stroke" />
            <text :x="dimension.textX" :y="dimension.textY" :font-size="fontSize" font-weight="600"
                text-anchor="middle" :fill="dimension.color" :transform="dimension.transform">
                {{ dimension.text }}
            </text>
        </g>

        <g :font-size="innerFontSize" font-weight="600" fill="#ffffff" text-anchor="middle">
            <text v-for="(label, index) in boxLabels" :key="`box-label-${index}`" :x="label.x" :y="label.y"
                :dominant-baseline="label.baseline" :transform="label.transform">
                {{ label.text }}
            </text>
        </g>
    </svg>
</template>
